import React, { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Card,
  Flex,
  Text,
  TextField,
  Button,
  Separator,
  IconButton,
  Badge,
  Select,
  Checkbox,
} from "@radix-ui/themes";
import { ArrowRightIcon } from "@radix-ui/react-icons";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "../../../context/ToastContext";
import { ToastAlert } from "components";
import { useProfileContext, useLocationContext } from "context";
import { useGetAllBrands, useSaveGoogleAdsMappings } from "hooks"; // same hook Klaviyo page used
import { useFeatureFlagProduct } from "../../../hooks/billing/useBilling";

export function GoogleAdsConfigSetupPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { setToastMessage } = useToast();
  const { userProfile } = useProfileContext();
  const { locationsData } = useLocationContext();
  const { data: brandsData } = useGetAllBrands(userProfile?.organisation_id);
  const saveGoogleAdsMappings = useSaveGoogleAdsMappings();

  // Google Ads configs passed in location.state.configs: [{ google_ads_account_id: '...' }, ...]
  const [googleConfigs, setGoogleConfigs] = useState([]);

  // Brand/locations split (mirrors Klaviyo logic)
  const [bconnectBrands, setBconnectBrands] = useState([]);
  const [independentLocations, setIndependentLocations] = useState([]);

  // mapping state keyed by location id -> { google_account_id, customer_list_name, isMapped }
  const [locationMappings, setLocationMappings] = useState({});
  const [originalLocationMappings, setOriginalLocationMappings] = useState({});

  // Brand-level temporary selection (apply-to-brand)
  const [brandAccountSelections, setBrandAccountSelections] = useState({});

  const [isLoading, setIsLoading] = useState(false);

  // Billing state
  const [billingAccepted, setBillingAccepted] = useState(false);
  
  // Get Google Ads feature flag product details
  const { data: googleAdsProductData, isLoading: isProductLoading, error: productError } = useFeatureFlagProduct("google_ads_feature_flag");

  // --- normalize configs from location.state
  useEffect(() => {
    const raw = location.state?.configs || [];
    const normalized = (Array.isArray(raw) ? raw : []).map((c, idx) => {
      const id = String(c.google_ads_account_id ?? c.googleAdsAccountId ?? "");
      const pretty = id.length === 10 ? `${id.slice(0, 3)}-${id.slice(3, 6)}-${id.slice(6)}` : id;
      return { id, label: `${pretty}` || id || `Account ${idx + 1}` };
    });
    setGoogleConfigs(normalized);
  }, [location.state]);

  // --- build brand lists & independent locations (mirror Klaviyo's logic)
  useEffect(() => {
    if (brandsData && locationsData) {
      const defaultBrand = brandsData.find((b) => b.is_default);
      const nonDefault = brandsData.filter((b) => !b.is_default);
      setBconnectBrands(nonDefault || []);
      const defaultLocations = (locationsData || []).filter(
        (loc) => loc.brand_id === defaultBrand?.id || loc.brand_id === null
      );
      setIndependentLocations(defaultLocations || []);
    }
  }, [brandsData, locationsData]);

  // Build brand -> locations map (same approach as Klaviyo)
  const brandLocationsMap = useMemo(() => {
    const map = {};
    if (!bconnectBrands) return map;
    bconnectBrands.forEach((b) => {
      const sourceBrand = (brandsData || []).find((bd) => String(bd.id) === String(b.id));
      if (sourceBrand && Array.isArray(sourceBrand.locations)) {
        map[b.id] = sourceBrand.locations;
      } else {
        map[b.id] = (locationsData || []).filter((loc) => String(loc.brand_id) === String(b.id));
      }
    });
    return map;
  }, [bconnectBrands, brandsData, locationsData]);

  // Initialize per-location mappings (NO automatic prefill — default empty string)
  useEffect(() => {
    if (!locationsData) return;
    const mappings = {};
    (locationsData || []).forEach((loc) => {
      const locId = loc.id ?? loc.value ?? loc.location_id;
      const key = String(locId);
      mappings[key] = {
        google_account_id: "", // intentionally empty to avoid unexpected prefill
        customer_list_name: "",
        isMapped: false,
      };
    });
    setLocationMappings(mappings);
    setOriginalLocationMappings(JSON.parse(JSON.stringify(mappings)));
  }, [locationsData, googleConfigs]);

  // Handlers
  const handleLocationAccountChange = (locationId, accountId) => {
    const key = String(locationId);
    setLocationMappings((prev) => {
      const existing = prev?.[key] || { google_account_id: "", customer_list_name: "", isMapped: false };
      return {
        ...prev,
        [key]: {
          ...existing,
          google_account_id: accountId,
          isMapped: Boolean(accountId && existing.customer_list_name && String(existing.customer_list_name).trim()),
        },
      };
    });
  };

  const handleLocationCustomerListChange = (locationId, listName) => {
    const key = String(locationId);
    setLocationMappings((prev) => {
      const existing = prev?.[key] || { google_account_id: "", customer_list_name: "", isMapped: false };
      return {
        ...prev,
        [key]: {
          ...existing,
          customer_list_name: listName,
          isMapped: Boolean(existing.google_account_id && listName && String(listName).trim()),
        },
      };
    });
  };

  const handleBrandAccountSelectionChange = (brandId, accountId) => {
    setBrandAccountSelections((prev) => ({ ...(prev || {}), [String(brandId)]: accountId }));
  };

  const handleApplyBrandAccount = (brandId) => {
    const selected = brandAccountSelections[String(brandId)];
    if (!selected) return;
    const brandLocs = brandLocationsMap[brandId] || [];
    setLocationMappings((prev) => {
      const next = { ...(prev || {}) };
      (brandLocs || []).forEach((loc) => {
        const locId = loc.id ?? loc.value ?? loc.location_id;
        const key = String(locId);
        const existing = next[key] || { google_account_id: "", customer_list_name: "", isMapped: false };
        next[key] = {
          ...existing,
          google_account_id: selected,
          isMapped: Boolean(selected && existing.customer_list_name && String(existing.customer_list_name).trim()),
        };
      });
      return next;
    });
  };

  // Helper: detect half-filled rows (one field present but not the other)
  const checkHalfFilled = (locs) => {
    return (locs || []).some((loc) => {
      const locId = loc.id ?? loc.value ?? loc.location_id;
      const key = String(locId);
      const curr = locationMappings[key] || { google_account_id: "", customer_list_name: "" };
      const hasAccount = Boolean(curr.google_account_id);
      const hasList = Boolean(curr.customer_list_name && String(curr.customer_list_name).trim());
      return (hasAccount && !hasList) || (!hasAccount && hasList);
    });
  };

  // Dirty detection for brands
  const hasBrandSectionChanges = useMemo(() => {
    const brandLocations = Object.values(brandLocationsMap).flat();
    return (brandLocations || []).some((loc) => {
      const locId = loc.id ?? loc.value ?? loc.location_id;
      const key = String(locId);
      const curr = locationMappings[key] || {};
      const orig = originalLocationMappings[key] || { google_account_id: "", customer_list_name: "" };
      if (!curr.google_account_id || !curr.customer_list_name) return false; // not fully populated
      return (
        String(curr.google_account_id) !== String(orig.google_account_id) ||
        String(curr.customer_list_name) !== String(orig.customer_list_name)
      );
    });
  }, [brandLocationsMap, locationMappings, originalLocationMappings]);

  // Dirty detection for independent locations
  const hasIndependentLocationChanges = useMemo(() => {
    const indep = independentLocations || [];
    return (indep || []).some((loc) => {
      const locId = loc.id ?? loc.value ?? loc.location_id;
      const key = String(locId);
      const curr = locationMappings[key] || {};
      const orig = originalLocationMappings[key] || { google_account_id: "", customer_list_name: "" };
      if (!curr.google_account_id || !curr.customer_list_name) return false;
      return (
        String(curr.google_account_id) !== String(orig.google_account_id) ||
        String(curr.customer_list_name) !== String(orig.customer_list_name)
      );
    });
  }, [independentLocations, locationMappings, originalLocationMappings]);

  const hasBrandHalfFilled = useMemo(() => checkHalfFilled(Object.values(brandLocationsMap).flat()), [brandLocationsMap, locationMappings]);
  const hasIndependentHalfFilled = useMemo(() => checkHalfFilled(independentLocations), [independentLocations, locationMappings]);

  // Billing calculation logic (flat-rate)
  const hasAnyConfiguration = useMemo(() => {
    const allLocations = [...(independentLocations || []), ...(Object.values(brandLocationsMap).flat() || [])];
    return allLocations.some((loc) => {
      const locId = loc.id ?? loc.value ?? loc.location_id;
      const key = String(locId);
      const curr = locationMappings[key] || {};
      // Show billing info if user has started configuring (either field filled)
      return curr.google_account_id || curr.customer_list_name;
    });
  }, [independentLocations, brandLocationsMap, locationMappings]);

  // Check if user has any complete configurations (both fields filled)
  const hasCompleteConfiguration = useMemo(() => {
    const allLocations = [...(independentLocations || []), ...(Object.values(brandLocationsMap).flat() || [])];
    return allLocations.some((loc) => {
      const locId = loc.id ?? loc.value ?? loc.location_id;
      const key = String(locId);
      const curr = locationMappings[key] || {};
      return curr.google_account_id && curr.customer_list_name;
    });
  }, [independentLocations, brandLocationsMap, locationMappings]);

  const monthlyPrice = googleAdsProductData?.data?.price || 0;
  const totalMonthlyCost = hasCompleteConfiguration ? monthlyPrice : 0;

  // Format currency helper
  const formatCurrency = (amount) => {
    return `£${parseFloat(amount).toFixed(2)}`;
  };

  // Save handler (uses fetch by default; replace endpoint/logic as needed)
  const handleSaveLocationMappings = async (section) => {
    try {
      setIsLoading(true);

      let selectedLocations = [];
      if (section === "brands") {
        selectedLocations = Object.values(brandLocationsMap).flat();
      } else if (section === "independent") {
        selectedLocations = independentLocations || [];
      } else {
        selectedLocations = [...(independentLocations || []), ...(Object.values(brandLocationsMap).flat() || [])];
      }

      const locationPayload = (selectedLocations || [])
        .map((loc) => {
          const locId = loc.id ?? loc.value ?? loc.location_id;
          const key = String(locId);
          const curr = locationMappings[key] || {};
          const currAccount = curr.google_account_id || null;
          const currList = curr.customer_list_name || null;
          const orig = originalLocationMappings[key] || {};
          const origAccount = orig.google_account_id || null;
          const origList = orig.customer_list_name || null;

          // require both fields to be present to save
          if (!currAccount || !currList) return null;

          // skip unchanged rows
          if (String(currAccount) === String(origAccount) && String(currList) === String(origList)) return null;

          return {
            bconnect_location_id: locId,
            bconnect_location_name: loc.name ?? loc.label ?? "",
            google_ads_account_id: currAccount,
            customer_list_name: currList,
          };
        })
        .filter(Boolean);

      if (locationPayload.length === 0) {
        setToastMessage("No location changes to save");
        setIsLoading(false);
        return;
      }

      const payload = {
        organisation_id: userProfile.organisation_id,
        brand_mappings: [],
        location_mappings: locationPayload,
      };

      const result = await saveGoogleAdsMappings.mutateAsync(payload);

      if (result?.data?.status !== "success") {
        throw new Error(result?.data?.message || "Failed to save location mappings");
      }

      queryClient.invalidateQueries(["google-ads-status"]);
      navigate("/activation-success", {
        state: {
          description: "Your locations will now sync with your selected Google Ads customer lists.",
          integrationType: "google-ads",
        },
      });
      setToastMessage("Location mappings saved successfully");
    } catch (err) {
      console.error("Error saving location mappings:", err);
      setToastMessage(err?.message || "Failed to save location mappings");
    } finally {
      setIsLoading(false);
    }
  };

  if (!location.state) {
    return <div>No authentication data found. Please go back and log your credentials again.</div>;
  }

  // Helper to render Select trigger label for an account id
  const accountLabel = (id) => {
    if (!id) return "Select account...";
    const found = googleConfigs.find((c) => String(c.id) === String(id));
    return found ? found.label : String(id);
  };

  // equal column width (match Atreemo/Klaviyo)
  const colStyle = { width: "32%", minWidth: 0 };

  return (
    <Flex style={{ minHeight: "100vh" }} justify="center" align="center" p="6">
      <Card style={{ maxWidth: "1000px", width: "100%" }} p="4">
        <Flex style={{ alignSelf: "stretch" }} display="flex" justify="between" align="center">
          <Flex display="flex" align="center" gap="4">
            <Flex
              style={{
                borderRadius: "var(--space-1)",
                background: "#FFFFFF",
                border: "1px solid var(--gray-a6)",
              }}
              height="40px"
              width="40px"
              p="2"
              justify="center"
              align="center"
            >
              <img src={require("../../../assets/images/google.png")} alt="google ads" style={{ width: "24px" }} />
            </Flex>
            <Flex direction="column" justify="center" align="start">
              <Text size="4" weight="medium">Google Ads</Text>
              <Text style={{ color: "var(--gray-a11)" }} size="1">
                Map b connect locations to Google Ads accounts and provide a customer list name.
              </Text>
            </Flex>
          </Flex>

          <Flex display="flex" align="center" gap="4">
            <Button size="2" variant="soft" onClick={() => navigate(-1)} color="gray">Cancel</Button>
            <Button
              size="2"
              variant="soft"
              onClick={() => handleSaveLocationMappings("all")}
              loading={isLoading}
              disabled={
                isLoading ||
                hasBrandHalfFilled ||
                hasIndependentHalfFilled ||
                !(hasBrandSectionChanges || hasIndependentLocationChanges) ||
                (hasCompleteConfiguration && !billingAccepted)
              }
            >
              Save and finish
            </Button>
          </Flex>
        </Flex>

        <Separator orientation="horizontal" size="4" my="4" />

        {/* BRANDS section */}
        <Flex style={{ alignSelf: "stretch" }} display="flex" align="start" gap="5" mb="4">
          <Flex style={{ width: "296px" }} direction="column" align="start" gap="2">
            <Text size="2" weight="medium">Brands</Text>
            <Text size="1" weight="regular">Group: b connect brands with each of their locations (map each location independently).</Text>
            <Text size="1" weight="regular"><strong>Note:</strong> You only need to complete rows for the locations you want to integrate. You can leave the rest blank.</Text>
          </Flex>

          <Flex direction="column" gap="3" style={{ width: "100%" }}>
            <Flex direction="row" justify="between" style={{ width: "100%" }}>
              <Text size="2" weight="medium" style={colStyle}>b connect location</Text>
              <Text size="2" weight="medium" style={{ ...colStyle, paddingLeft: "8px" }}>Google Ads account</Text>
              <Text size="2" weight="medium" style={{ ...colStyle, paddingLeft: "13px" }}>Customer list name</Text>
            </Flex>

            {(bconnectBrands || []).length > 0 ? (
              (bconnectBrands || []).map((b) => {
                const brandLocations = brandLocationsMap[b.id] || [];
                return (
                  <Flex key={b.id} direction="column" gap="2" style={{ width: "100%" }}>
                    <Flex direction="row" style={{ width: "100%", alignItems: "center", gap: 8, justifyContent: "space-between" }}>
                      <Badge size="2" variant="soft" style={{flexShrink: 0, flexGrow: 0, width: "auto", maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}} title={b.name}>
                        {b.name}
                      </Badge>

                      {(brandLocationsMap[b.id] || []).length > 0 && (
                        <Flex style={{ marginLeft: "8px", gap: 8, alignItems: "center", flexShrink: 0 }}>
                          <Select.Root
                            value={brandAccountSelections[String(b.id)] || ""}
                            onValueChange={(v) => handleBrandAccountSelectionChange(b.id, v)}
                            style={{ width: 220, minWidth: 120, flexShrink: 0 }}
                          >
                            <Select.Trigger placeholder="Apply account to brand..." style={{ width: "220px", boxSizing: "border-box" }}>
                              <div style={{ textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", width: "100%" }} title={brandAccountSelections[String(b.id)] ? accountLabel(brandAccountSelections[String(b.id)]) : "Apply account to brand..."}>
                                {brandAccountSelections[String(b.id)] ? accountLabel(brandAccountSelections[String(b.id)]) : "Apply account to brand..."}
                              </div>
                            </Select.Trigger>
                            <Select.Content side="bottom" align="start" sideOffset={4}>
                              {googleConfigs.map((gc) => <Select.Item key={gc.id} value={gc.id} title={gc.label}>{gc.label}</Select.Item>)}
                            </Select.Content>
                          </Select.Root>

                          <Button size="1" variant="soft" onClick={() => handleApplyBrandAccount(b.id)} disabled={!brandAccountSelections[String(b.id)]} style={{ flexShrink: 0 }}>
                            Apply
                          </Button>
                        </Flex>
                      )}
                    </Flex>

                    {(brandLocations || []).length > 0 ? (
                      brandLocations.map((loc) => {
                        const locId = loc.id ?? loc.value ?? loc.location_id;
                        const key = String(locId);
                        const mapping = locationMappings[key] || {};

                        return (
                          <Flex key={key} direction="row" justify="between" style={{ width: "100%" }}>
                            <Flex style={colStyle}>
                              <Flex style={{ width: "100%", backgroundColor: "var(--gray-3)", border: "1px solid var(--gray-a7)", borderRadius: "var(--radius-2)", padding: "0 var(--space-2)", height: "34px", alignItems: "center", overflow: "hidden" }}>
                                <Text size="2" title={loc.name} style={{ textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", width: "100%", color: "var(--gray-a11)" }}>
                                  {loc.name}
                                </Text>
                              </Flex>
                            </Flex>

                            <Flex style={{ paddingLeft: "8px", paddingRight: "8px", height: "34px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                              <IconButton size="2" variant="ghost" color="gray" style={{ pointerEvents: "none", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                <ArrowRightIcon width="16" height="16" />
                              </IconButton>
                            </Flex>

                            <Flex style={{ ...colStyle }}>
                              <Select.Root value={mapping.google_account_id || ""} onValueChange={(v) => handleLocationAccountChange(key, v)} style={{ width: "100%" }}>
                                <Select.Trigger placeholder="Select account..." style={{ width: "100%", boxSizing: "border-box" }}>
                                  <div style={{ textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", width: "100%" }} title={mapping.google_account_id ? accountLabel(mapping.google_account_id) : "Select a Google Ads account"}>
                                    {mapping.google_account_id ? accountLabel(mapping.google_account_id) : "Select a Google Ads account"}
                                  </div>
                                </Select.Trigger>
                                <Select.Content side="bottom" align="start" sideOffset={4}>
                                  {googleConfigs.map((gc) => <Select.Item key={gc.id} value={gc.id} title={gc.label}>{gc.label}</Select.Item>)}
                                </Select.Content>
                              </Select.Root>
                            </Flex>

                            <Flex style={{ paddingLeft: "8px", paddingRight: "8px", height: "34px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                              <IconButton size="2" variant="ghost" color="gray" style={{ pointerEvents: "none", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                <ArrowRightIcon width="16" height="16" />
                              </IconButton>
                            </Flex>

                            <Flex style={colStyle}>
                              <TextField.Root
                                value={mapping.customer_list_name || ""}
                                onChange={(e) => handleLocationCustomerListChange(key, e.target.value)}
                                placeholder="Enter customer list name"
                                size="2"
                                variant="surface"
                                style={{ width: "100%" }}
                              />
                            </Flex>
                          </Flex>
                        );
                      })
                    ) : (
                      <Flex justify="center" style={{ padding: "8px 0 16px 0" }}>
                        <Text size="2" style={{ color: "var(--gray-a9)" }}>No locations for this brand</Text>
                      </Flex>
                    )}
                  </Flex>
                );
              })
            ) : (
              <Flex justify="center" style={{ padding: "20px 0" }}>
                <Text>No brands found.</Text>
              </Flex>
            )}
          </Flex>
        </Flex>

        <Separator orientation="horizontal" size="4" my="2" />

        {/* INDEPENDENT LOCATIONS */}
        <Flex style={{ alignSelf: "stretch" }} display="flex" align="start" gap="5">
          <Flex style={{ width: "296px" }} direction="column" align="start" gap="2">
            <Text size="2" weight="medium">Locations</Text>
            <Text size="1" weight="regular">Map independent b connect locations to a Google Ads account & customer list name.</Text>
            <Text size="1" weight="regular"><strong>Note:</strong> You only need to complete rows for the locations you want to integrate. You can leave the rest blank.</Text>
          </Flex>

          <Flex direction="column" gap="3" style={{ width: "100%" }}>
            <Flex direction="row" justify="between" style={{ width: "100%" }}>
              <Text size="2" weight="medium" style={colStyle}>b connect location</Text>
              <Text size="2" weight="medium" style={{ ...colStyle, paddingLeft: "8px" }}>Google Ads account</Text>
              <Text size="2" weight="medium" style={{ ...colStyle, paddingLeft: "13px" }}>Customer list name</Text>
            </Flex>

            {(independentLocations || []).length > 0 ? (
              (independentLocations || []).map((loc) => {
                const locId = loc.id ?? loc.value ?? loc.location_id;
                const key = String(locId);
                const mapping = locationMappings[key] || {};
                return (
                  <Flex key={key} direction="row" justify="between" style={{ width: "100%" }}>
                    <Flex style={colStyle}>
                      <Flex style={{ width: "100%", backgroundColor: "var(--gray-3)", border: "1px solid var(--gray-a7)", borderRadius: "var(--radius-2)", padding: "0 var(--space-2)", height: "34px", alignItems: "center", overflow: "hidden" }}>
                        <Text size="2" title={loc.name} style={{ textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", width: "100%", color: "var(--gray-a11)" }}>
                          {loc.name}
                        </Text>
                      </Flex>
                    </Flex>

                    <Flex style={{ paddingLeft: "8px", paddingRight: "8px", height: "34px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <IconButton size="2" variant="ghost" color="gray" style={{ pointerEvents: "none", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <ArrowRightIcon width="16" height="16" />
                      </IconButton>
                    </Flex>

                    <Flex style={{ ...colStyle }}>
                      <Select.Root value={mapping.google_account_id || ""} onValueChange={(v) => handleLocationAccountChange(key, v)} style={{ width: "100%" }}>
                        <Select.Trigger placeholder="Select account..." style={{ width: "100%", boxSizing: "border-box" }}>
                          <div style={{ textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", width: "100%" }} title={mapping.google_account_id ? accountLabel(mapping.google_account_id) : "Select a Google Ads account"}>
                            {mapping.google_account_id ? accountLabel(mapping.google_account_id) : "Select a Google Ads account"}
                          </div>
                        </Select.Trigger>
                        <Select.Content side="bottom" align="start" sideOffset={4}>
                          {googleConfigs.map((gc) => <Select.Item key={gc.id} value={gc.id} title={gc.label}>{gc.label}</Select.Item>)}
                        </Select.Content>
                      </Select.Root>
                    </Flex>

                    <Flex style={{ paddingLeft: "8px", paddingRight: "8px", height: "34px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <IconButton size="2" variant="ghost" color="gray" style={{ pointerEvents: "none", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <ArrowRightIcon width="16" height="16" />
                      </IconButton>
                    </Flex>

                    <Flex style={colStyle}>
                      <TextField.Root
                        value={mapping.customer_list_name || ""}
                        onChange={(e) => handleLocationCustomerListChange(key, e.target.value)}
                        placeholder="Enter customer list name"
                        size="2"
                        variant="surface"
                        style={{ width: "100%" }}
                      />
                    </Flex>
                  </Flex>
                );
              })
            ) : (
              <Flex justify="center" style={{ padding: "20px 0" }}>
                <Text>No locations found.</Text>
              </Flex>
            )}
          </Flex>
        </Flex>

        {/* Billing Confirmation Section - Always show for paid integrations */}
        {googleConfigs.length > 0 && (
          <Flex
            direction="column"
            gap="3"
            p="3"
            mt="4"
            style={{
              border: "1px solid var(--orange-a7)",
              borderRadius: "var(--radius-3)",
              backgroundColor: "var(--orange-a2)",
              width: "100%"
            }}
          >
            <Text size="3" weight="medium" color="orange">
              Billing Information
            </Text>
            
            
            <Flex direction="column" gap="2">
              <Flex justify="between" align="center">
                <Text size="2">
                  Google Ads integration
                </Text>
                <Text size="2" weight="medium">
                  {formatCurrency(monthlyPrice)}/month
                </Text>
              </Flex>
            </Flex>

            <Flex align="center" gap="2" mt="2">
              <Checkbox
                checked={billingAccepted}
                onCheckedChange={setBillingAccepted}
                size="2"
              />
              <Text size="2">
                I understand and accept the monthly billing charge of {formatCurrency(monthlyPrice)} for Google Ads integration
              </Text>
            </Flex>

            {productError && (
              <Text size="2" color="red">
                Unable to load pricing information. Please try again.
              </Text>
            )}

            {isProductLoading && (
              <Text size="2" color="gray">
                Loading pricing information...
              </Text>
            )}
          </Flex>
        )}
      </Card>

      <div style={{ position: "fixed", zIndex: 2147483647, pointerEvents: "none" }}>
        <ToastAlert />
      </div>
    </Flex>
  );
}
